import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  File as FileIcon,
  Image as ImageIcon,
  Paperclip,
  Plus,
  Search,
  Send,
  X,
} from "lucide-react"
import { router, usePage } from "@inertiajs/react"
import { createConsumer, type Subscription } from "@rails/actioncable"
import { toast } from "sonner"

import { AssistantHistory } from "@/components/assistant/assistant-history"
import { AssistantMarkdown } from "@/components/assistant/assistant-markdown"
import {
  useAssistant,
  useAssistantEnabled,
} from "@/components/assistant/assistant-provider"
import { DraftCard, type DraftView } from "@/components/assistant/draft-card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { InsetPane } from "@/components/inset-pane"
import { cn } from "@/lib/utils"
import {
  account_billing_path,
  assistant_chat_messages_path,
  assistant_chat_path,
  assistant_chats_path,
  assistant_drafts_path,
} from "@/routes"

interface ToolCallView {
  id: number | string
  name: string
  summary: string
}

interface ChatMessage {
  id: number | string
  role: "user" | "assistant" | "system" | "tool"
  content: string | null
  tool_calls?: ToolCallView[]
  drafts?: DraftView[]
}

const DRAFT_KEY_PREFIX = "assistant_draft:"
const DRAFT_NEW_KEY = `${DRAFT_KEY_PREFIX}new`
const DEFAULT_TITLE = "New chat"

const ATTACHMENT_ACCEPT = [
  // Images
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  // PDF
  "application/pdf",
  // Office (Word / Excel / PowerPoint, old + new)
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Text
  "text/plain",
  "text/markdown",
  "text/csv",
].join(",")

function draftKey(chatUid: string | null): string {
  return `${DRAFT_KEY_PREFIX}${chatUid ?? "new"}`
}

function readDraft(chatUid: string | null): string {
  if (typeof window === "undefined") return ""
  return window.localStorage.getItem(draftKey(chatUid)) ?? ""
}

function writeDraft(chatUid: string | null, prompt: string): void {
  if (typeof window === "undefined") return
  const key = draftKey(chatUid)
  if (prompt.trim()) {
    window.localStorage.setItem(key, prompt)
  } else {
    window.localStorage.removeItem(key)
  }
}

function csrfToken(): string {
  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  )
}

/**
 * Mounted on every page by the persistent layout. On a desk with the
 * assistant off it renders nothing and runs none of the panel's hooks, so
 * no chat is fetched and no cable subscription is opened (INCO-512).
 */
export function AssistantPanel() {
  const enabled = useAssistantEnabled()
  if (!enabled) return null

  return <EnabledAssistantPanel />
}

function EnabledAssistantPanel() {
  const { assistantOpen, closeAssistant } = useAssistant()
  const { props } = usePage()
  const account = props.current_account as { uid: string } | undefined
  // The allowance (INCO-321). Shared on every page by BaseAccountController,
  // so the composer is right on first paint rather than after a fetch.
  const allowance = props.assistant_usage as AssistantAllowance | undefined

  const [chatUid, setChatUid] = useState<string | null>(null)
  const [chatTitle, setChatTitle] = useState<string>(DEFAULT_TITLE)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [streamingContent, setStreamingContent] = useState<string | null>(null)
  // Tools the assistant reaches for mid-turn, shown as they happen. Cleared on
  // completion, when the server sends back the turn with these same calls
  // attached to the messages that persisted them.
  const [streamingTools, setStreamingTools] = useState<ToolCallView[]>([])
  const [prompt, setPrompt] = useState<string>(() => readDraft(null))
  const [attachments, setAttachments] = useState<File[]>([])
  // Proposals an agent left on the desk from somewhere else (INCO-366). They
  // belong to no conversation here, so they cannot ride in on `messages` —
  // they sit above them until the broker settles each one.
  const [pending, setPending] = useState<DraftView[]>([])

  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null)
  const subscriptionRef = useRef<Subscription | null>(null)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (!consumerRef.current) {
      consumerRef.current = createConsumer()
    }
    return () => {
      subscriptionRef.current?.unsubscribe()
      consumerRef.current?.disconnect()
      consumerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!chatUid || !consumerRef.current) return
    const consumer = consumerRef.current
    const subscription = consumer.subscriptions.create(
      { channel: "AssistantChatChannel", uid: chatUid },
      {
        received(data: {
          type: "chunk" | "tool_call" | "completed" | "error" | "title"
          text?: string
          name?: string
          summary?: string
          messages?: ChatMessage[]
          message?: string
          title?: string
        }) {
          if (data.type === "chunk") {
            setStreamingContent((prev) => (prev ?? "") + (data.text ?? ""))
          } else if (data.type === "tool_call") {
            setStreamingTools((prev) => [
              ...prev,
              {
                id: `tool-${prev.length}`,
                name: data.name ?? "",
                summary: data.summary ?? "",
              },
            ])
          } else if (data.type === "completed") {
            // A tool-using turn persists several rows, so the server sends the
            // whole turn rather than one message — the same shape a reload
            // renders, tool calls included.
            setMessages((prev) => [...prev, ...(data.messages ?? [])])
            setStreamingContent(null)
            setStreamingTools([])
          } else if (data.type === "title") {
            if (data.title) setChatTitle(data.title)
          } else if (data.type === "error") {
            toast.error(data.message ?? "Something went wrong.")
            setStreamingContent(null)
            setStreamingTools([])
          }
        },
      },
    )
    subscriptionRef.current = subscription
    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
    }
  }, [chatUid])

  // Persist the in-progress draft under the active chat key (or "new").
  // The companion writeDraft removes the key when the prompt is empty so the
  // saved state stays accurate without a stale entry per chat.
  useEffect(() => {
    writeDraft(chatUid, prompt)
  }, [prompt, chatUid])

  // When the active chat changes (e.g. clicking + or switching from history),
  // hydrate the prompt from that chat's saved draft. Initial mount is skipped
  // because the useState initializer already loaded the "new" draft.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setPrompt(readDraft(chatUid))
    setAttachments([])
  }, [chatUid])

  const ensureChat = useCallback(async (): Promise<string | null> => {
    if (chatUid) return chatUid
    if (!account) return null
    const res = await fetch(assistant_chats_path(account.uid), {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    })
    if (!res.ok) {
      toast.error("Failed to start chat")
      return null
    }
    const data = (await res.json()) as { uid: string }
    // The "new" draft was just consumed by sending it as the first message.
    // Drop the key so it doesn't leak into the next blank-state chat.
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(DRAFT_NEW_KEY)
    }
    setChatUid(data.uid)
    return data.uid
  }, [account, chatUid])

  const sendMessage = useCallback(
    async (content: string, files: File[]) => {
      const cid = await ensureChat()
      if (!cid || !account) return false

      const body = new FormData()
      body.append("content", content)
      files.forEach((f) => body.append("attachments[]", f))

      const res = await fetch(assistant_chat_messages_path(account.uid, cid), {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (!res.ok) {
        // 402 is the allowance, and the server's sentence is the one to show:
        // it knows whether this is a cap, a plan or a failed card, and the
        // three want different answers from the reader.
        if (res.status === 402) {
          const body = (await res.json().catch(() => ({}))) as { error?: string }
          toast.error(body.error ?? "The assistant is paused for this period.")
          // Re-reads the shared prop, which flips the composer to the notice.
          router.reload({ only: ["assistant_usage"] })
          return false
        }
        toast.error("Failed to send message")
        return false
      }
      const data = (await res.json()) as {
        chat: { uid: string; title: string }
        user_message: ChatMessage
      }
      setMessages((prev) => [...prev, data.user_message])
      setChatTitle(data.chat.title || DEFAULT_TITLE)
      setStreamingContent("")
      setStreamingTools([])
      return true
    },
    [account, ensureChat],
  )

  const startNewChat = useCallback(() => {
    // Save whatever the user has been drafting against the chat they're
    // leaving, so it's there when they come back to it via history.
    writeDraft(chatUid, prompt)

    subscriptionRef.current?.unsubscribe()
    subscriptionRef.current = null
    setChatUid(null)
    setChatTitle(DEFAULT_TITLE)
    setMessages([])
    setStreamingContent(null)
    setStreamingTools([])
    setPrompt("")
    setAttachments([])
  }, [prompt, chatUid])

  const loadChat = useCallback(
    async (uid: string) => {
      if (!account) return
      if (uid === chatUid) return

      // Persist the in-flight draft against the chat we're leaving.
      writeDraft(chatUid, prompt)

      try {
        const res = await fetch(assistant_chat_path(account.uid, uid), {
          headers: { Accept: "application/json" },
        })
        if (!res.ok) {
          toast.error("Failed to load chat")
          return
        }
        const data = (await res.json()) as {
          chat: { uid: string; title: string }
          messages: ChatMessage[]
        }

        subscriptionRef.current?.unsubscribe()
        subscriptionRef.current = null
        setStreamingContent(null)
        setStreamingTools([])
        setMessages(data.messages)
        setChatTitle(data.chat.title || DEFAULT_TITLE)
        // Setting chatUid triggers the cable subscription + draft hydration
        // effects already wired in this component.
        setChatUid(uid)
      } catch {
        toast.error("Failed to load chat")
      }
    },
    [account, chatUid, prompt],
  )

  // An approved draft comes back from the approval endpoint; swap it into the
  // message that carries it so the card flips to its quiet done state.
  const updateDraft = useCallback((updated: DraftView) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.drafts
          ? {
              ...m,
              drafts: m.drafts.map((d) => (d.uid === updated.uid ? updated : d)),
            }
          : m,
      ),
    )
    // An agent's draft leaves the waiting list the moment it is settled: the
    // list is things still to answer, and a finished row in it reads as one
    // more thing to do.
    setPending((prev) => prev.filter((d) => d.uid !== updated.uid))
  }, [])

  // Fetched when the panel opens, not on every navigation — this is a short
  // list that changes when an agent acts, not when the broker clicks around.
  useEffect(() => {
    if (!assistantOpen || !account) return
    let cancelled = false

    void fetch(assistant_drafts_path(account.uid), { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : { drafts: [] }))
      .then((data: { drafts: DraftView[] }) => {
        if (!cancelled) setPending(data.drafts)
      })
      .catch(() => {
        // A waiting list that failed to load is not worth a toast over the
        // conversation the broker actually came here for.
      })

    return () => {
      cancelled = true
    }
  }, [assistantOpen, account])

  const isStreaming = streamingContent !== null

  return (
    <aside
      data-print-hide
      aria-label="AI assistant"
      aria-hidden={!assistantOpen}
      inert={!assistantOpen}
      className={cn(
        "flex overflow-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none md:bg-sidebar",
        // The panel was `w-96 shrink-0` at every width — 384px, wider than a
        // 375px phone — and it is a flex sibling of the whole app, so opening
        // it pushed the page off-screen rather than covering it. Below `md` it
        // is a full-screen overlay; from `md` up it is the column it always was.
        assistantOpen
          ? "fixed inset-0 z-50 w-full md:sticky md:inset-auto md:top-0 md:z-auto md:h-screen md:w-96 md:shrink-0"
          : "hidden md:block md:h-screen md:w-0 md:shrink-0",
      )}
    >
      {/* The same floating container as the inset main content — the same
          recipe on the same element: margins on the card, m-2 with ml-0 (the
          8px gap to its left neighbour comes from that neighbour's margin,
          exactly as SidebarInset does it). Below md it is a full-screen
          overlay and stays full-bleed. */}
      <InsetPane className="min-w-0 flex-1 md:m-2 md:ml-0">
        <header className="flex h-pagehead shrink-0 items-center justify-between border-b px-3">
          <h2 className="truncate text-sm font-medium" title={chatTitle}>
            {chatTitle}
          </h2>
          <div className="flex items-center gap-1">
            <Button size="icon-sm"
              variant="ghost"
              type="button"
              aria-label="Start new chat"
              onClick={startNewChat}
            >
              <Plus className="w-4 h-4" />
            </Button>
            {account && (
              <AssistantHistory
                accountUid={account.uid}
                currentChatUid={chatUid}
                onSelectChat={loadChat}
              />
            )}
            <Button size="icon-sm"
              variant="ghost"
              type="button"
              aria-label="Close AI assistant"
              onClick={closeAssistant}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {pending.length > 0 && account && (
          <section className="border-b px-3 py-2.5">
            <p className="text-muted-foreground text-xs font-medium">
              Waiting on you
            </p>
            <div className="mt-2 flex flex-col gap-2">
              {pending.map((draft) => (
                <DraftCard
                  key={draft.uid}
                  draft={draft}
                  accountUid={account.uid}
                  onChange={updateDraft}
                />
              ))}
            </div>
          </section>
        )}

        <MessageList
          messages={messages}
          streamingContent={streamingContent}
          streamingTools={streamingTools}
          isStreaming={isStreaming}
          accountUid={account?.uid}
          onDraftChange={updateDraft}
        />

        {allowance?.capped ? (
          <AllowanceNotice allowance={allowance} accountUid={account?.uid} />
        ) : (
          <>
            {allowance?.warning && (
              <p className="text-muted-foreground px-4 pb-1 text-xs" data-assistant-allowance="warning">
                {allowance.remaining.toLocaleString()} assistant requests left this period.
              </p>
            )}
            <PromptInput
              prompt={prompt}
              attachments={attachments}
              onPromptChange={setPrompt}
              onAttachmentsChange={setAttachments}
              onSend={sendMessage}
              disabled={isStreaming}
            />
          </>
        )}
      </InsetPane>
    </aside>
  )
}

interface MessageListProps {
  messages: ChatMessage[]
  streamingContent: string | null
  streamingTools: ToolCallView[]
  isStreaming: boolean
  accountUid?: string
  onDraftChange: (updated: DraftView) => void
}

function MessageList({
  messages,
  streamingContent,
  streamingTools,
  isStreaming,
  accountUid,
  onDraftChange,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages.length, streamingContent, streamingTools.length])

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-6 text-sm text-muted-foreground">
        <p className="text-center">How can I help?</p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <ul className="flex flex-col gap-3 text-sm">
        {messages.map((m) => (
          <li key={m.id} className="flex flex-col gap-1.5">
            <ToolTrail calls={m.tool_calls ?? []} />
            {accountUid &&
              (m.drafts ?? []).map((draft) => (
                <DraftCard
                  key={draft.uid}
                  draft={draft}
                  accountUid={accountUid}
                  onChange={onDraftChange}
                />
              ))}
            {/* A message that only carried tool calls has no body to render. */}
            {m.content && <MessageBubble role={m.role} content={m.content} />}
          </li>
        ))}
        {isStreaming && (
          <li className="flex flex-col gap-1.5">
            <ToolTrail calls={streamingTools} />
            <MessageBubble
              role="assistant"
              content={streamingContent ?? ""}
              streaming
            />
          </li>
        )}
      </ul>
      <div ref={bottomRef} />
    </div>
  )
}

// What the assistant looked up, as a quiet trail above its answer. Deliberately
// not a pill or a badge — DESIGN.md keeps status as faint text and reserves
// accent colour for the one primary action on screen.
function ToolTrail({ calls }: { calls: ToolCallView[] }) {
  if (calls.length === 0) return null

  return (
    <ul className="flex flex-col gap-1 border-l pl-2.5 text-xs text-muted-foreground">
      {calls.map((call) => (
        <li key={call.id} className="flex items-center gap-1.5">
          <Search aria-hidden className="size-3 shrink-0 text-muted-foreground" />
          <span className="truncate" title={call.summary}>
            {call.summary}
          </span>
        </li>
      ))}
    </ul>
  )
}

function MessageBubble({
  role,
  content,
  streaming,
}: {
  role: ChatMessage["role"]
  content: string
  streaming?: boolean
}) {
  const isUser = role === "user"
  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-3 py-2",
          // The broker's own text stays literal — asterisks they typed are
          // asterisks they meant. Only the assistant writes markdown.
          isUser
            ? "whitespace-pre-wrap bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        {isUser ? (
          content
        ) : content ? (
          <AssistantMarkdown content={content} />
        ) : streaming ? (
          "…"
        ) : (
          ""
        )}
      </div>
    </div>
  )
}

interface PromptInputProps {
  prompt: string
  attachments: File[]
  onPromptChange: (next: string) => void
  onAttachmentsChange: (next: File[]) => void
  onSend: (content: string, attachments: File[]) => Promise<boolean>
  disabled?: boolean
}

function PromptInput({
  prompt,
  attachments,
  onPromptChange,
  onAttachmentsChange,
  onSend,
  disabled,
}: PromptInputProps) {
  const [pending, setPending] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Drag enter/leave fire for every descendant. A simple counter keeps the
  // "isDragOver" indicator stable while the user moves over child elements.
  const dragCounter = useRef(0)

  const canSend = useMemo(
    () => !pending && !disabled && (prompt.trim().length > 0 || attachments.length > 0),
    [pending, disabled, prompt, attachments.length],
  )

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return
      onAttachmentsChange([...attachments, ...incoming])
    },
    [attachments, onAttachmentsChange],
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []))
    e.target.value = ""
  }

  const removeAttachment = (idx: number) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== idx))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSend) return
    setPending(true)
    const ok = await onSend(prompt.trim(), attachments)
    setPending(false)
    if (ok) {
      onPromptChange("")
      onAttachmentsChange([])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    if (disabled) return
    if (!e.dataTransfer?.types.includes("Files")) return
    e.preventDefault()
    dragCounter.current += 1
    setIsDragOver(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return
    if (!e.dataTransfer?.types.includes("Files")) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return
    if (!e.dataTransfer?.types.includes("Files")) return
    e.preventDefault()
    dragCounter.current = Math.max(0, dragCounter.current - 1)
    if (dragCounter.current === 0) setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return
    e.preventDefault()
    dragCounter.current = 0
    setIsDragOver(false)
    addFiles(Array.from(e.dataTransfer?.files ?? []))
  }

  return (
    <form
      onSubmit={handleSubmit}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "relative border-t p-3 transition-colors",
        isDragOver && "bg-primary/5 ring-2 ring-inset ring-primary/40",
      )}
    >
      {isDragOver && (
        /* design-lint-ok: the drop overlay is a single accent element, the one DESIGN.md allows per view */
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-xs font-medium text-primary">
          Drop files to attach
        </div>
      )}
      {attachments.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {attachments.map((file, idx) => (
            <AttachmentChip
              key={`${file.name}-${idx}`}
              file={file}
              onRemove={() => removeAttachment(idx)}
            />
          ))}
        </ul>
      )}
      <Textarea
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Ask anything…"
        rows={2}
        disabled={disabled}
        className="resize-none border-0 px-0 shadow-none focus-visible:ring-0"
      />
      <div className="mt-2 flex items-center justify-between">
        <Button size="icon-sm"
          variant="ghost"
          type="button"
          aria-label="Attach files"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <Paperclip className="w-4 h-4" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={handleFileChange}
        />
        <Button size="icon-sm"
          type="submit"
          aria-label="Send message"
          disabled={!canSend}
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </form>
  )
}

function AttachmentChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith("image/")
  const TypeIcon = isImage ? ImageIcon : FileIcon
  return (
    <li className="group flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs">
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        className="relative inline-block size-3.5 text-muted-foreground transition-colors hover:text-foreground"
      >
        <TypeIcon
          aria-hidden
          className="absolute inset-0 size-3.5 transition-opacity group-hover:opacity-0"
        />
        <X
          aria-hidden
          className="absolute inset-0 size-3.5 opacity-0 transition-opacity group-hover:opacity-100"
        />
      </button>
      <span className="max-w-40 truncate" title={file.name}>
        {file.name}
      </span>
    </li>
  )
}

export interface AssistantAllowance {
  used: number
  allowance: number
  remaining: number
  capped: boolean
  warning: boolean
  addon_active: boolean
  addon: { name: string; price_label: string; cadence: string; requests: number; blurb: string }
}

/**
 * What the panel says instead of a composer once the allowance is gone
 * (INCO-321).
 *
 * Politely, and with the number: a broker who hits a wall with no explanation
 * does not upgrade, they leave. It replaces the box rather than disabling it —
 * a greyed-out textarea invites somebody to type into it and find out.
 */
function AllowanceNotice({ allowance, accountUid }: { allowance: AssistantAllowance; accountUid?: string }) {
  return (
    <div className="border-border border-t px-4 py-3.5" data-assistant-allowance="capped">
      <p className="text-sm font-medium">
        You have used all {allowance.allowance.toLocaleString()} assistant requests this period.
      </p>
      <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
        It stops here rather than billing you for more. The allowance resets at the start of your next period
        {!allowance.addon_active && `, or ${allowance.addon.name} adds ${allowance.addon.requests.toLocaleString()} for ${allowance.addon.price_label} ${allowance.addon.cadence}`}.
      </p>
      {accountUid && (
        <Button asChild size="sm" variant="outline" className="mt-2.5">
          <a href={account_billing_path(accountUid)}>Open billing</a>
        </Button>
      )}
    </div>
  )
}
